/**
 * SvgSlide — draws the drawing primitives computed by the SERVER deck engine
 * (POST /api/deck-studio/preview). This component contains no layout logic
 * at all: every x/y/w/h, color, and font size arrives from deckKit, so the
 * preview cannot drift from the exported PowerPoint. Charts are simplified
 * visual stand-ins for the native PowerPoint charts the export contains.
 *
 * Coordinates arrive in inches on a 13.333 x 7.5 page; we scale x100 into the
 * SVG viewBox. Wrapped text uses foreignObject so the browser word-wraps the
 * same strings the PPTX will wrap.
 */

import React from 'react';

const S = 100; // 1 inch → 100 SVG units
const API = process.env.REACT_APP_API_URL || '';

const hx = (c) => `#${c}`;

function TextPrim({ p }) {
  const align = p.align || 'left';
  const justify = p.valign === 'middle' ? 'center' : p.valign === 'bottom' ? 'flex-end' : 'flex-start';
  return (
    <foreignObject x={p.x * S} y={p.y * S} width={Math.max(p.w * S, 1)} height={Math.max(p.h * S, 1)}>
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: justify,
          overflow: 'hidden', fontFamily: `'Segoe UI', system-ui, sans-serif`,
          fontSize: `${(p.size * S) / 72}px`, lineHeight: 1.28,
          color: hx(p.color), fontWeight: p.bold ? 700 : 400,
          fontStyle: p.italic ? 'italic' : 'normal', textAlign: align,
          letterSpacing: p.caps || p.charSpacing ? '0.08em' : 'normal',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}
      >
        {p.text}
      </div>
    </foreignObject>
  );
}

function ChartPrim({ p, theme }) {
  const x = p.x * S, y = p.y * S, w = p.w * S, h = p.h * S;
  const series = p.series || [];
  const labels = p.labels || [];
  const opts = p.opts || {};
  const muted = hx(theme?.color?.muted || '7C8A84');
  if (!series.length || !labels.length) return null;

  // Missing months arrive as null and must stay gaps, never zeros: a null
  // plotted against a tightened 90-100 axis would shoot far outside the box.
  const allVals = series.flatMap((s) => s.values || []).filter((v) => Number.isFinite(v));
  const maxV = Math.max(opts.valMax || 0, ...(allVals.length ? allVals : [1]), 1);
  const minV = Math.min(0, ...(allVals.length ? allVals : [0]));
  const span = maxV - minV || 1;

  const padL = opts.spark ? 2 : 30, padB = opts.spark ? 2 : 18, padT = 6, padR = 4;
  const cw = w - padL - padR, ch = h - padT - padB;
  const loV = opts.valMin != null ? opts.valMin : minV;
  const hiV = opts.valMax != null ? opts.valMax : maxV;
  const spanV = hiV - loV || 1;
  // Clamped to the plot box: whatever the data does, ink never leaves the card.
  const yOf = (v) => {
    const raw = y + padT + ch - ((v - loV) / spanV) * ch;
    return Math.max(y + padT - 2, Math.min(y + padT + ch + 2, raw));
  };
  const bench = opts.benchmark || (opts.target != null ? { value: opts.target } : null);
  const benchLine = bench && bench.value != null && p.chartType !== 'doughnut' && p.chartType !== 'bar' ? (
    <line x1={x + padL} y1={yOf(bench.value)} x2={x + w - padR} y2={yOf(bench.value)}
      stroke={muted} strokeWidth="1.4" strokeDasharray="6 4" />
  ) : null;

  if (p.chartType === 'doughnut') {
    const vals = series[0].values || [];
    const tot = vals.reduce((a, b) => a + b, 0) || 1;
    const cx = x + w * 0.38, cy = y + h / 2, r = Math.min(w * 0.3, h * 0.42);
    let a0 = -Math.PI / 2;
    return (
      <g>
        <text x={cx} y={cy + 2} fontSize={Math.min(34, r * 0.8)} fontWeight="700" fill={hx(theme.color.ink)} textAnchor="middle">{tot}</text>
        <text x={cx} y={cy + Math.min(34, r * 0.8) * 0.85} fontSize="10" fill={muted} textAnchor="middle">total</text>
        {vals.map((v, i) => {
          const a1 = a0 + (v / tot) * Math.PI * 2;
          const large = a1 - a0 > Math.PI ? 1 : 0;
          const p0 = [cx + r * Math.cos(a0), cy + r * Math.sin(a0)];
          const p1 = [cx + r * Math.cos(a1), cy + r * Math.sin(a1)];
          const path = `M ${p0[0]} ${p0[1]} A ${r} ${r} 0 ${large} 1 ${p1[0]} ${p1[1]}`;
          const el = <path key={i} d={path} fill="none" stroke={hx(theme.chart[i % theme.chart.length])} strokeWidth={r * 0.38} />;
          a0 = a1;
          return el;
        })}
        {labels.slice(0, 5).map((l, i) => (
          <g key={`l${i}`}>
            <rect x={x + w * 0.72} y={y + h * 0.2 + i * 16} width={9} height={9} fill={hx(theme.chart[i % theme.chart.length])} />
            <text x={x + w * 0.72 + 13} y={y + h * 0.2 + i * 16 + 8.5} fontSize="11" fill={muted}>{l}</text>
          </g>
        ))}
      </g>
    );
  }

  const horizontal = p.chartType === 'bar';
  if (horizontal) {
    const vals = series[0].values || [];
    const rowH = ch / labels.length;
    return (
      <g>
        {labels.map((l, i) => (
          <g key={i}>
            <text x={x + padL - 4} y={y + padT + i * rowH + rowH * 0.62} fontSize="10" fill={muted} textAnchor="end">{String(l).slice(0, 12)}</text>
            <rect x={x + padL} y={y + padT + i * rowH + rowH * 0.18} width={Math.max(1, ((vals[i] || 0) / maxV) * cw)} height={rowH * 0.58} fill={hx(series[0].color)} rx="1.5" />
          </g>
        ))}
      </g>
    );
  }

  if (p.chartType === 'line' || p.chartType === 'area' || opts.spark) {
    const step = cw / Math.max(labels.length - 1, 1);
    const single = series.length === 1 && !opts.spark;
    return (
      <g>
        {!opts.spark && [0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={x + padL} y1={y + padT + ch * f} x2={x + w - padR} y2={y + padT + ch * f} stroke="#E4E8E6" strokeWidth="0.75" />
        ))}
        {benchLine}
        {series.map((s, si) => {
          // Only observed months draw; the line connects across a gap rather
          // than dropping to an invented zero.
          const present = (s.values || []).map((v, i) => [v, i]).filter(([v]) => Number.isFinite(v));
          const pts = present.map(([v, i]) => `${x + padL + i * step},${yOf(v)}`).join(' ');
          if (!present.length) return null;
          return (
            <g key={si}>
              {p.chartType === 'area' && (
                <polygon points={`${x + padL + present[0][1] * step},${yOf(loV)} ${pts} ${x + padL + present[present.length - 1][1] * step},${yOf(loV)}`} fill={hx(s.color)} opacity="0.18" />
              )}
              <polyline points={pts} fill="none" stroke={hx(s.color)} strokeWidth={opts.spark ? 2 : 2.5} strokeLinejoin="round" strokeLinecap="round" />
              {single && present.map(([v, i]) => (
                <g key={`pt${i}`}>
                  <circle cx={x + padL + i * step} cy={yOf(v)} r="3" fill={hx(s.color)} />
                  {opts.dataLabels && <text x={x + padL + i * step} y={yOf(v) - 6} fontSize="9" fontWeight="700" fill={hx(theme.color.ink)} textAnchor="middle">{v}</text>}
                </g>
              ))}
            </g>
          );
        })}
        {!opts.spark && labels.map((l, i) => (
          i % Math.ceil(labels.length / 8) === 0 ? (
            <text key={i} x={x + padL + i * step} y={y + h - 4} fontSize="10" fill={muted} textAnchor="middle">{String(l).slice(0, 8)}</text>
          ) : null
        ))}
      </g>
    );
  }

  // column (default), stacked or clustered
  const groupW = cw / labels.length;
  return (
    <g>
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1={x + padL} y1={y + padT + ch * f} x2={x + w - padR} y2={y + padT + ch * f} stroke="#E4E8E6" strokeWidth="0.75" />
      ))}
      {benchLine}
      {labels.map((l, i) => {
        if (opts.stacked || opts.percent) {
          const tot = opts.percent ? series.reduce((a, s) => a + (s.values[i] || 0), 0) || 1 : 1;
          let acc = 0;
          return (
            <g key={i}>
              {series.map((s, si) => {
                const v = opts.percent ? ((s.values[i] || 0) / tot) * maxV : (s.values[i] || 0);
                const y1 = yOf(acc + v), y0 = yOf(acc);
                acc += v;
                return <rect key={si} x={x + padL + i * groupW + groupW * 0.22} y={y1} width={groupW * 0.56} height={Math.max(0.5, y0 - y1)} fill={hx(s.color)} />;
              })}
              <text x={x + padL + i * groupW + groupW / 2} y={y + h - 4} fontSize="10" fill={muted} textAnchor="middle">{String(l).slice(0, 8)}</text>
            </g>
          );
        }
        const bw = (groupW * 0.7) / series.length;
        return (
          <g key={i}>
            {series.map((s, si) => (
              <rect key={si} x={x + padL + i * groupW + groupW * 0.15 + si * bw} y={yOf(s.values[i] || 0)} width={Math.max(1, bw - 1.5)} height={Math.max(0.5, yOf(minV) - yOf(s.values[i] || 0))} fill={hx(s.color)} rx="1.5" />
            ))}
            <text x={x + padL + i * groupW + groupW / 2} y={y + h - 4} fontSize="10" fill={muted} textAnchor="middle">{String(l).slice(0, 8)}</text>
          </g>
        );
      })}
      {opts.showLegend && series.map((s, si) => (
        <g key={`lg${si}`}>
          <rect x={x + padL + si * 110} y={y + h + 2} width={9} height={9} fill={hx(s.color)} />
          <text x={x + padL + si * 110 + 13} y={y + h + 10.5} fontSize="10" fill={muted}>{String(s.name).slice(0, 14)}</text>
        </g>
      ))}
    </g>
  );
}

function TablePrim({ p, theme }) {
  const header = p.header || [];
  const rows = p.rows || [];
  const totalW = (p.colW || []).reduce((a, b) => a + b, 0) || p.w;
  return (
    <foreignObject x={p.x * S} y={p.y * S} width={p.w * S} height={(7.0 - p.y) * S}>
      <table xmlns="http://www.w3.org/1999/xhtml" style={{ borderCollapse: 'collapse', width: '100%', fontFamily: `'Segoe UI', system-ui, sans-serif`, fontSize: `${(p.fontSize * S) / 72}px` }}>
        <thead>
          <tr>
            {header.map((c, i) => (
              <th key={i} style={{ background: hx(c.fill || theme.color.brand), color: c.color ? hx(c.color) : '#fff', textAlign: c.align || 'left', padding: '5px 8px', width: `${((p.colW[i] || 1) / totalW) * 100}%`, fontWeight: 700 }}>{c.text}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} style={{ background: hx(c.fill || 'FFFFFF'), color: hx(c.color || theme.color.ink), padding: p.rowPad != null ? `${Math.max(1, (p.rowPad / 2) * S)}px 6px` : '4px 8px', border: '0.5px solid #E4E8E6', fontWeight: c.bold ? 600 : 400 }}>{c.text}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </foreignObject>
  );
}

const FULL_BLEED = new Set(['cover', 'section', 'callout', 'closing']);

export default function SvgSlide({ page, theme, width = 640, pageNumber = null }) {
  if (!page || !theme) return null;
  const H = (width * 7.5) / 13.333;
  const showFooter = !FULL_BLEED.has(page.archetype);
  return (
    <svg viewBox="0 0 1333.3 750" width={width} height={H} style={{ display: 'block', background: hx(theme.color.paper), borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.18)' }}>
      {showFooter && (
        <g>
          <line x1={62} y1={714} x2={1271} y2={714} stroke="#D8DEDB" strokeWidth="1" />
          <text x={62} y={732} fontSize="12" fill={hx(theme.color.muted)}>{theme.footer || ''}</text>
          {pageNumber != null && <text x={1271} y={732} fontSize="12" fill={hx(theme.color.muted)} textAnchor="end">{pageNumber}</text>}
        </g>
      )}
      {page.primitives.map((p, i) => {
        switch (p.t) {
          case 'rect':
            return <rect key={i} x={p.x * S} y={p.y * S} width={p.w * S} height={p.h * S} fill={p.fill === 'none' ? 'none' : hx(p.fill)} rx={(p.radius || 0) * S} stroke={p.line ? hx(p.line) : 'none'} strokeWidth={p.lineW || 0} />;
          case 'poly':
            return <polygon key={i} points={(p.points || []).map((q) => `${q[0] * S},${q[1] * S}`).join(' ')} fill={hx(p.fill)} fillOpacity={p.alpha == null ? 1 : p.alpha} stroke="none" />;
          case 'line':
            return <line key={i} x1={p.x1 * S} y1={p.y1 * S} x2={p.x2 * S} y2={p.y2 * S} stroke={hx(p.color)} strokeWidth={p.width * 1.4} strokeDasharray={p.dash ? '6 4' : 'none'} />;
          case 'text':
            return <TextPrim key={i} p={p} />;
          case 'chart':
            return <ChartPrim key={i} p={p} theme={theme} />;
          case 'table':
            return <TablePrim key={i} p={p} theme={theme} />;
          case 'image':
            return p.kind === 'logo' ? (
              <image key={i} href={`${API}/api/deck-studio/branding/logo`} x={p.x * S} y={p.y * S} width={p.w * S} height={p.h * S} preserveAspectRatio="xMidYMid meet" />
            ) : null;
          default:
            return null;
        }
      })}
    </svg>
  );
}
